// src/components/pages/LandingPage/b2b/ServiceProcess.js
import React, { useEffect, useRef, useState } from "react";
import { Box, Container, Typography, useMediaQuery } from "@mui/material";
import { getPrimaryColor } from "../../../GlobalFunction/Theme";
import { getLangFromCookie, LANG_COOKIE } from "../../../../Utils/LangCookie";

/* ---------------- language helper ---------------- */
const getCurrentLang = () =>
  getLangFromCookie() || localStorage.getItem(LANG_COOKIE) || "ZH";

/* ---------------- section title ---------------- */
const SECTION_TITLE = {
  ZH: "服務流程",
  EN: "Service Process",
  JP: "サービスの流れ",
};

/*
 * Colored background boxes used by the enhanced Service Process
 * layout.
 *
 * The colors repeat in this order when there are more rows than
 * configured colors.
 */
const PROCESS_BOX_COLORS = ["#005F91", "#438FC7", "#35A5AA"];

/*
 * Default enhanced-row appearance.
 *
 * The assigned PROCESS_BOX_COLORS color is shown only while the row
 * is hovered or keyboard-focused.
 */
const PROCESS_CAPSULE_DEFAULT_BACKGROUND = "#F5F6F5";

const PROCESS_CAPSULE_DEFAULT_BORDER = "#ECEEED";

const PROCESS_CAPSULE_DEFAULT_TEXT = "#4F565B";

const PROCESS_CAPSULE_PRIMARY_COLOR = getPrimaryColor("b2b");

/*
 * Hover changes only colors.
 *
 * The capsule, image, and text do not move or resize during hover.
 */
/*
 * Hover colors begin immediately.
 *
 * A short duration preserves a smooth visual change without making
 * the text appear to wait after the pointer enters the row.
 */
const PROCESS_CAPSULE_HOVER_DURATION_MS = 120;

/*
 * Supported colored-box reveal directions.
 *
 * opposite:
 * The box starts from the side opposite the image.
 *
 * same:
 * The box starts from the same side as the image.
 */
const PROCESS_BOX_REVEAL_DIRECTIONS = ["opposite", "same"];

const DEFAULT_PROCESS_BOX_REVEAL_DIRECTION = "opposite";

/*
 * The enhanced layered layout is now enabled for all screen widths.
 *
 * EnhancedProcessRow contains xs / sm / md / lg sizing so the same
 * desktop-style layout can be used on mobile, tablet vertical,
 * tablet horizontal, and desktop.
 */
const PROCESS_ENHANCED_LAYOUT_MEDIA_QUERY = "(min-width: 0px)";

/*
 * Centralized animation sequence for the enhanced layout.
 *
 * Sequence:
 * image → colored box → title → description
 *
 * All values are milliseconds.
 */
const PROCESS_ANIMATION_TIMING = {
  /*
   * Stage 1:
   * The capsule enters first from the side of the row.
   *
   * The key remains named `box` temporarily so the current enhanced
   * component continues working until it is refactored in Step 3B.
   */
  box: {
    delay: 0,
    duration: 950,
  },

  /*
   * Stage 2:
   * The process image appears after the capsule is already moving
   * into place.
   */
  image: {
    delay: 420,
    duration: 800,
  },

  /*
   * Stage 3:
   * The text number and title appear together.
   */
  title: {
    delay: 820,
    duration: 620,
  },

  /*
   * Stage 4:
   * The description appears last.
   */
  text: {
    delay: 1080,
    duration: 680,
  },
};

/*
 * Only the colored background extends behind the process image.
 *
 * The title and description remain inside the safe content column.
 */
const PROCESS_BOX_IMAGE_OVERLAP = {
  md: 32,
  lg: 48,
};

/*
 * Guaranteed space between the process image and the title/content.
 */
const PROCESS_CONTENT_IMAGE_GAP = {
  md: 24,
  lg: 32,
};

/**
 * ✅ Put number images here:
 * /public/img/LandingPage/b2b/processNums/1.webp ... 6.webp
 */
const NUMBER_IMG = {
  "01": "/img/LandingPage/b2b/1.webp",
  "02": "/img/LandingPage/b2b/2.webp",
  "03": "/img/LandingPage/b2b/3.webp",
  "04": "/img/LandingPage/b2b/4.webp",
  "05": "/img/LandingPage/b2b/5.webp",
  "06": "/img/LandingPage/b2b/6.webp",
};

/* ---------------- items with ZH / EN / JP ---------------- */
const SERVICE_PROCESS_ITEMS = [
  {
    id: "01",
    title: {
      ZH: "01 初步諮詢",
      EN: "01 Consultation",
      JP: "01 初回相談",
    },
    text: {
      ZH: "深入了解您的產業現況、痛點與目標，提供初步策略方向建議。",
      EN: "We take time to understand your industry, problems and goals, and offer an initial strategic direction.",
      JP: "お客様の業界状況や課題、目標を丁寧にお伺いし、初期段階の戦略方針をご提案いたします。",
    },
    img: "/img/products/B2B/process/process01.webp",
  },
  {
    id: "02",
    title: {
      ZH: "02 勘查評估",
      EN: "02 Site Assessment",
      JP: "02 現地調査・評価",
    },
    text: {
      ZH: "實地盤點空間、流程與風險點，建立客觀可量化的評估基準。",
      EN: "We inspect the site, workflows and problems to build an objective, quantifiable assessment baseline.",
      JP: "現場にて空間・動線・リスク箇所を確認し、客観的かつ定量的な評価基準を構築いたします。",
    },
    img: "/img/products/B2B/process/process02.webp",
  },
  {
    id: "03",
    title: {
      ZH: "03 客製化方案",
      EN: "03 Solution Planning",
      JP: "03 カスタマイズプラン",
    },
    text: {
      ZH: "依需求量身打造最適切的服務方案與執行架構。",
      EN: "Based on your specific needs, we develop a customized service plan and execution framework.",
      JP: "お客様のニーズに合わせて、最適なサービスプランと実行体制を個別に設計いたします。",
    },
    img: "/img/products/B2B/process/process03.webp",
  },
  {
    id: "04",
    title: {
      ZH: "04 成本分析",
      EN: "04 Cost Analysis",
      JP: "04 コスト分析",
    },
    text: {
      ZH: "清楚提供預算配置與投入分析，確保效益最大化。",
      EN: "We provide clear budget allocation and input analysis to help you maximize overall benefits.",
      JP: "ご予算配分や投資内容を分かりやすくお示しし、収益を最大化できるようご提案いたします。",
    },
    img: "/img/products/B2B/process/process04.webp",
  },
  {
    id: "05",
    title: {
      ZH: "05 合作執行",
      EN: "05 Collaboration",
      JP: "05 共同実施",
    },
    text: {
      ZH: "與貴合作夥伴共同執行，專案人員進場導入，確保品質落實。",
      EN: "In collaboration with your team, our specialists carry out on-site implementation to ensure high-quality results.",
      JP: "確かな品質を担保するため、貴社のパートナーとして共に歩み、専門チームが導入作業を確実に遂行します。",
    },
    img: "/img/products/B2B/process/process05.webp",
  },
  {
    id: "06",
    title: {
      ZH: "06 追蹤服務",
      EN: "06 Follow-up Service",
      JP: "06 フォローアップサービス",
    },
    text: {
      ZH: "定期回訪，成效追蹤與優化調整，建立長期合作夥伴關係。",
      EN: "We review performance regularly and optimize the plan to support long-term collaboration.",
      JP: "定期的なコンタクト（訪問・電話）による徹底したフォローで、長期的なパートナーシップを築きます。",
    },
    img: "/img/products/B2B/process/process06.webp",
  },
];

/* ---------- helpers ---------- */
function stripLeadingNumber(title) {
  return String(title || "").replace(/^\s*\d+\s*/, "");
}

function normalizeBoxRevealDirection(direction) {
  const normalizedDirection = String(direction || "")
    .trim()
    .toLowerCase();

  return PROCESS_BOX_REVEAL_DIRECTIONS.includes(normalizedDirection)
    ? normalizedDirection
    : DEFAULT_PROCESS_BOX_REVEAL_DIRECTION;
}

/* ---------- hook: in-view once ---------- */
function useInViewOnce({ threshold = 0.25, rootMargin = "0px 0px -15% 0px" }) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (!ref.current || inView) return;

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          obs.disconnect();
        }
      },
      { threshold, rootMargin },
    );

    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [inView, threshold, rootMargin]);

  return { ref, inView };
}

/* ---------- animation wrapper ---------- */
function MotionBox({ show, from, delay = 5, children, sx }) {
  const distance = 24;
  const transform = show
    ? "translate3d(0,0,0)"
    : from === "left"
      ? `translate3d(-${distance}px,0,0)`
      : from === "right"
        ? `translate3d(${distance}px,0,0)`
        : `translate3d(0,${distance}px,0)`;

  return (
    <Box
      sx={{
        opacity: show ? 1 : 0,
        transform,
        transition: `opacity 1200ms ease, transform 3000ms cubic-bezier(.2,.8,.2,1)`,
        transitionDelay: `${delay}ms`,
        willChange: "opacity, transform",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

/* ---------- CURRENT MOBILE / TABLET-PORTRAIT ROW ---------- */
function LegacyProcessRow({ item, index, lang, fonts, prefersReducedMotion }) {
  const isEven = index % 2 === 0;
  const { ref, inView } = useInViewOnce({});
  const show = prefersReducedMotion ? true : inView;

  const rawTitle = item.title?.[lang] || item.title?.ZH || "";
  const titleText = stripLeadingNumber(rawTitle);
  const text = item.text?.[lang] || item.text?.ZH || "";
  const numberImg = NUMBER_IMG[item.id];

  // match Service.js sizing style
  const titleFont = { xs: 13, sm: 22, md: 28, lg: 30 };

  return (
    <Box ref={ref} sx={{ mb: { xs: 3, md: 6 } }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: {
            xs: isEven ? "row" : "row-reverse",
            md: isEven ? "row" : "row-reverse",
          },
          alignItems: "center",
          gap: { xs: 1.5, sm: 3, md: 6 },
        }}
      >
        {/* text block */}
        <MotionBox
          show={show}
          from={isEven ? "left" : "right"}
          sx={{ flex: 1, minWidth: 0 }}
        >
          {/* ✅ title group: number image + title text */}
          <Box
            sx={{
              display: "flex",
              justifyContent: isEven ? "flex-end" : "flex-start",
              mb: { xs: 0.75, md: 1.5 },
            }}
          >
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "baseline", // ✅ vertically centered to number image
                gap: { xs: 0.8, md: 1.2 },
                maxWidth: {
                  xs: "100%",
                  sm: "100%",
                  md: "100%",
                  lg: "86%",
                },

                minWidth: 0,
              }}
            >
              {numberImg ? (
                <Box
                  component="img"
                  src={numberImg}
                  alt={`${item.id}.`}
                  loading="lazy"
                  fetchPriority="low"
                  decoding="async"
                  sx={{
                    // slightly bigger than text height (match Service.js feel)
                    height: { xs: 25, sm: 25, md: 58 },
                    width: "auto",
                    display: "block",
                    flexShrink: 0,
                  }}
                />
              ) : null}

              <Typography
                component="span"
                sx={{
                  fontFamily: fonts,
                  fontWeight: 700,
                  fontSize: titleFont,
                  color: "text.primary",
                  textAlign: isEven ? "right" : "left",
                  lineHeight: 1, // ✅ keep optical center alignment
                  display: "inline-flex",
                  alignItems: "baseline",
                  wordBreak: "break-word",
                }}
              >
                {titleText}
              </Typography>
            </Box>
          </Box>

          <Typography
            sx={{
              fontFamily: fonts,
              fontWeight: 400,
              fontSize: { xs: 11.5, sm: 15, md: 16 },
              lineHeight: { xs: 1.6, md: 1.8 },
              color: "text.secondary",
              textAlign: isEven ? "right" : "left",
            }}
          >
            {text}
          </Typography>
        </MotionBox>

        {/* image block */}
        <MotionBox show={show} from={isEven ? "right" : "left"} delay={170}>
          <Box
            sx={{
              flex: "0 0 auto",
              width: { xs: 120, sm: 280, md: 350, lg: 800 },
              height: { xs: 90, sm: 210, md: "auto", lg: "auto" },
              maxWidth: { xs: 140, sm: 320, md: 350, lg: 800 },
              borderRadius: 1.5,
              overflow: "hidden",
            }}
          >
            <Box
              component="img"
              src={item.img}
              alt={titleText}
              loading="lazy"
              fetchPriority="low"
              decoding="async"
              sx={{
                display: "block",
                width: "100%",
                height: { xs: "100%", md: "100%" },
                objectFit: "cover",

              transform: "scale(1)",

              transition:
                "transform 520ms cubic-bezier(.2,.8,.2,1)",

              willChange: "transform",
              }}
            />
          </Box>
        </MotionBox>
      </Box>
    </Box>
  );
}

/* ---------- DESKTOP / TABLET-LANDSCAPE ROW ---------- */
function EnhancedProcessRow({
  item,
  index,
  lang,
  fonts,
  boxColor = "#005F91",
  animationTiming = PROCESS_ANIMATION_TIMING,
  prefersReducedMotion = false,
}) {
  const isEven = index % 2 === 0;

  const { ref, inView } = useInViewOnce({
    threshold: 0.2,
    rootMargin: "0px 0px -12% 0px",
  });

  const show = prefersReducedMotion || inView;

  const capsuleTiming = animationTiming?.box || PROCESS_ANIMATION_TIMING.box;

  const imageTiming = animationTiming?.image || PROCESS_ANIMATION_TIMING.image;

  const titleTiming = animationTiming?.title || PROCESS_ANIMATION_TIMING.title;

  const textTiming = animationTiming?.text || PROCESS_ANIMATION_TIMING.text;

  const rawTitle = item.title?.[lang] || item.title?.ZH || "";

  const titleText = stripLeadingNumber(rawTitle);

  const text = item.text?.[lang] || item.text?.ZH || "";

  /*
   * Convert IDs such as "01" through "06" into visible text numbers
   * such as 1 through 6.
   *
   * LegacyProcessRow continues using the existing NUMBER_IMG files.
   */
  const displayNumber = String(Number.parseInt(item.id, 10) || index + 1);

  /*
   * Move the capsule by its complete width.
   *
   * Because the capsule is already attached to the viewport edge:
   *
   * Image-left row:
   * translateX(-100%) places it completely outside the left edge.
   *
   * Image-right row:
   * translateX(100%) places it completely outside the right edge.
   */
  const capsuleHiddenTransform = isEven
    ? "translate3d(-100%, -50%, 0)"
    : "translate3d(100%, -50%, 0)";

  return (
    <Box
      ref={ref}
      sx={{
        position: "relative",

        /*
         * Escape the Container padding so the enhanced row can use
         * the complete viewport width.
         */
        width: "100vw",
        ml: "calc(50% - 50vw)",

        mb: {
          xs: 5,
          sm: 6,
          md: 7,
          lg: 10,
        },
      }}
    >
      <Box
        tabIndex={0}
        sx={{
          position: "relative",

          "--process-hover-color": boxColor,

          display: "grid",

          gridTemplateColumns: {
            xs: isEven
              ? "10px 170px minmax(0, 1fr) 20px"
              : "20px minmax(0, 1fr) 170px 10px",

            sm: isEven
              ? "minmax(0, 1fr) 250px 330px minmax(0, 1fr)"
              : "minmax(0, 1fr) 330px 250px minmax(0, 1fr)",

            md: isEven
              ? "minmax(0, 1fr) 300px 580px minmax(0, 1fr)"
              : "minmax(0, 1fr) 580px 300px minmax(0, 1fr)",

            lg: isEven
              ? "minmax(0, 1fr) 340px 640px minmax(0, 1fr)"
              : "minmax(0, 1fr) 640px 340px minmax(0, 1fr)",
          },

          alignItems: "center",

          width: "100%",

          minHeight: {
            xs: 170,
            sm: 210,
            md: 230,
            lg: 260,
          },

          outline: "none",

          isolation: "isolate",

          "&:focus-visible": {
            outline: `3px solid ${PROCESS_CAPSULE_PRIMARY_COLOR}`,
            outlineOffset: 6,
          },

          "@media (min-width: 1200px)": {
            "&:focus-visible .service-process-capsule": {
              backgroundColor: "var(--process-hover-color)",

              borderColor: "var(--process-hover-color)",

              boxShadow: "0 18px 46px rgba(0, 61, 94, 0.16)",
            },

            "&:focus-visible .service-process-number, &:focus-visible .service-process-title, &:focus-visible .service-process-description":
              {
                color: "#ffffff",
              },

            "&:focus-visible .service-process-divider::after": {
              transform: "scaleX(1)",
            },

            "&:focus-visible .service-process-image-frame img": {
              transform: "scale(1.055)",
            },
          },

          "@media (hover: hover) and (pointer: fine) and (min-width: 1200px)": {
            "&:hover .service-process-capsule": {
              backgroundColor: "var(--process-hover-color)",

              borderColor: "var(--process-hover-color)",

              boxShadow: "0 18px 46px rgba(0, 61, 94, 0.16)",
            },

            "&:hover .service-process-number, &:hover .service-process-title, &:hover .service-process-description":
              {
                color: "#ffffff",
              },

            "&:hover .service-process-divider::after": {
              transform: "scaleX(1)",
            },

            "&:hover .service-process-image-frame img": {
              transform: "scale(1.055)",
            },
          },
        }}
      >
        {/* =========================================
            ANIMATED CAPSULE BACKGROUND
        ========================================== */}
        <Box
          aria-hidden="true"
          className="service-process-capsule"
          sx={{
            position: "absolute",

            top: "50%",

            left: isEven
              ? 0
              : {
                  xs: 10,
                  sm: "max(20px, calc((100% - 580px) / 2))",
                  md: "calc((100% - 880px) / 2)",
                  lg: "calc((100% - 980px) / 2)",
                },

            right: isEven
              ? {
                  xs: 10,
                  sm: "max(20px, calc((100% - 580px) / 2))",
                  md: "calc((100% - 880px) / 2)",
                  lg: "calc((100% - 980px) / 2)",
                }
              : 0,

            zIndex: 0,

            height: {
              xs: 170,
              sm: 172,
              md: 190,
              lg: 220,
            },

            bgcolor: {
              xs: "var(--process-hover-color)",
              sm: "var(--process-hover-color)",
              md: "var(--process-hover-color)",
              lg: PROCESS_CAPSULE_DEFAULT_BACKGROUND,
            },

            border: "1px solid",

            borderColor: {
              xs: "var(--process-hover-color)",
              sm: "var(--process-hover-color)",
              md: "var(--process-hover-color)",
              lg: PROCESS_CAPSULE_DEFAULT_BORDER,
            },

            borderRadius: isEven
              ? {
                  xs: "0 90px 90px 0",
                  sm: "0 86px 86px 0",
                  md: "0 95px 95px 0",
                  lg: "0 110px 110px 0",
                }
              : {
                  xs: "90px 0 0 90px",
                  sm: "86px 0 0 86px",
                  md: "95px 0 0 95px",
                  lg: "110px 0 0 110px",
                },

            opacity: show ? 1 : 0,

            transform: show
              ? "translate3d(0, -50%, 0)"
              : capsuleHiddenTransform,

            transition: prefersReducedMotion
              ? [
                  `background-color ${PROCESS_CAPSULE_HOVER_DURATION_MS}ms ease`,
                  `border-color ${PROCESS_CAPSULE_HOVER_DURATION_MS}ms ease`,
                  `box-shadow ${PROCESS_CAPSULE_HOVER_DURATION_MS}ms ease`,
                ].join(", ")
              : [
                  `opacity ${capsuleTiming.duration}ms ease-out`,
                  `transform ${capsuleTiming.duration}ms cubic-bezier(.16,.84,.24,1)`,
                  `background-color ${PROCESS_CAPSULE_HOVER_DURATION_MS}ms ease`,
                  `border-color ${PROCESS_CAPSULE_HOVER_DURATION_MS}ms ease`,
                  `box-shadow ${PROCESS_CAPSULE_HOVER_DURATION_MS}ms ease`,
                ].join(", "),

            transitionDelay: prefersReducedMotion
              ? "0ms"
              : `${capsuleTiming.delay}ms`,

            boxShadow: {
              xs: "0 18px 46px rgba(0, 61, 94, 0.16)",
              sm: "0 18px 46px rgba(0, 61, 94, 0.16)",
              md: "0 18px 46px rgba(0, 61, 94, 0.16)",
              lg: "0 14px 38px rgba(0, 61, 94, 0.06)",
            },

            willChange: "opacity, transform, background-color",

            pointerEvents: "none",
          }}
        />

        {/* =========================================
            PROCESS IMAGE
        ========================================== */}
        <Box
          className="service-process-image-frame"
          sx={{
            position: "relative",

            top: {
              xs: 0,
              sm: -34,
              md: -24,
              lg: -35,
            },

            gridRow: "1",

            gridColumn: isEven ? "2" : "3",

            zIndex: 3,

            width: "100%",

            maxWidth: {
              xs: 180,
              sm: 250,
              md: 300,
              lg: 340,
            },

            /*
             * The image is taller than the capsule, so it extends
             * slightly above and below the capsule like the reference.
             */
            aspectRatio: "16 / 9",

            justifySelf: isEven ? "start" : "end",

            /*
             * Leave a small flat capsule edge visible before the
             * image begins.
             */
            ml: isEven
              ? {
                  xs: 0.75,
                  sm: 1,
                  md: 1,
                  lg: 1.5,
                }
              : 0,

            mr: isEven
              ? 0
              : {
                  xs: 0.75,
                  sm: 1,
                  md: 1,
                  lg: 1.5,
                },

            opacity: show ? 1 : 0,

            transform: show
              ? "translate3d(0, 0, 0)"
              : isEven
                ? "translate3d(-34px, 0, 0)"
                : "translate3d(34px, 0, 0)",

            transition: prefersReducedMotion
              ? "none"
              : [
                  `opacity ${imageTiming.duration}ms ease`,
                  `transform ${imageTiming.duration}ms cubic-bezier(.2,.82,.2,1)`,
                ].join(", "),

            transitionDelay: prefersReducedMotion
              ? "0ms"
              : `${imageTiming.delay}ms`,

            willChange: "opacity, transform",

            borderRadius: 1.5,

            overflow: "hidden",

            boxShadow: "0 18px 44px rgba(0, 0, 0, 0.12)",
          }}
        >
          <Box
            component="img"
            src={item.img}
            alt={titleText}
            loading="lazy"
            fetchPriority="low"
            decoding="async"
            sx={{
              display: "block",

              width: "100%",
              height: "100%",

              objectFit: "cover",

              transform: "scale(1)",

              transition:
                "transform 520ms cubic-bezier(.2,.8,.2,1)",

              willChange: "transform",
            }}
          />
        </Box>

        {/* =========================================
            CAPSULE CONTENT
        ========================================== */}
        <Box
          sx={{
            position: "relative",

            gridRow: "1",

            gridColumn: isEven ? "3" : "2",

            zIndex: 2,

            width: "100%",
            minWidth: 0,

            minHeight: {
              xs: 124,
              sm: 160,
              md: 190,
              lg: 220,
            },

            /*
             * Horizontal padding is applied separately to the heading,
             * divider, and description so the divider can begin
             * directly beside the image.
             *
             * On mobile, add a small safe gap on the image side so
             * text never visually collides with the floating image.
             */
            px: 0,

            pl: isEven
              ? {
                  xs: 1.5,
                  sm: 0,
                  lg: 4,
                }
              : 0,

            pr: isEven
              ? 0
              : {
                  xs: 1.5,
                  sm: 0,
                  lg: 4,
                },

            py: {
              xs: 0.75,
              sm: 1,
              md: 1.25,
              lg: 1.5,
            },

            boxSizing: "border-box",

            display: "flex",
            flexDirection: "column",
            justifyContent: "center",

            /*
             * The heading and description position themselves toward
             * the rounded end according to the alternating row.
             */
            alignItems: "stretch",

            textAlign: "left",
          }}
        >
          {/* =====================================
              NUMBER IMAGE + TITLE
          ====================================== */}
          <Box
            className="service-process-heading"
            sx={{
              width: "100%",

              display: "flex",

              justifyContent: isEven ? "flex-end" : "flex-start",

              /*
               * Keep the heading away from the curved capsule edge.
               */
              pr: isEven
                ? {
                    xs: 2,
                    sm: 6,
                    md: 5,
                    lg: 7,
                  }
                : 0,

              pl: isEven
                ? 0
                : {
                    xs: 2,
                    sm: 6,
                    md: 5,
                    lg: 7,
                  },

              opacity: show ? 1 : 0,

              transform: show
                ? "translate3d(0, 0, 0)"
                : "translate3d(0, 16px, 0)",

              transition: prefersReducedMotion
                ? "none"
                : [
                    `opacity ${titleTiming.duration}ms ease`,
                    `transform ${titleTiming.duration}ms cubic-bezier(.2,.8,.2,1)`,
                  ].join(", "),

              transitionDelay: prefersReducedMotion
                ? "0ms"
                : `${titleTiming.delay}ms`,

              willChange: "opacity, transform",
            }}
          >
            <Box
              sx={{
                display: "inline-flex",

                /*
                 * The number always appears before the title.
                 *
                 * The complete heading group is still positioned on
                 * the correct side through its parent justification.
                 */
                flexDirection: "row",

                alignItems: "baseline",

                gap: {
                  xs: 0.75,
                  sm: 1,
                  md: 1,
                  lg: 2,
                },

                maxWidth: {
                  xs: "100%",
                  sm: "100%",
                  md: "100%",
                  lg: "86%",
                },

                minWidth: 0,
              }}
            >
              <Typography
                component="span"
                className="service-process-number"
                aria-hidden="true"
                sx={{
                  m: 0,

                  flexShrink: 0,

                  fontFamily: fonts,
                  fontWeight: 700,
                  fontStyle: "italic",

                  /*
                   * The number is intentionally larger than the title.
                   */
                  fontSize: {
                    xs: 24,
                    sm: 36,
                    md: 48,
                    lg: 60,
                  },

                  lineHeight: 0.9,

                  color: {
                    xs: "#ffffff",
                    sm: "#ffffff",
                    md: "#ffffff",
                    lg: PROCESS_CAPSULE_PRIMARY_COLOR,
                  },

                  textAlign: "center",

                  transition: `color ${PROCESS_CAPSULE_HOVER_DURATION_MS}ms ease`,

                  willChange: "color",
                }}
              >
                {displayNumber}
              </Typography>

              <Typography
                component="h3"
                className="service-process-title"
                sx={{
                  m: 0,

                  fontFamily: fonts,
                  fontWeight: 700,

                  fontSize: {
                    xs: 14,
                    sm: 20,
                    md: 24,
                    lg: 35,
                  },

                  lineHeight: 1.2,

                  color: {
                    xs: "#ffffff",
                    sm: "#ffffff",
                    md: "#ffffff",
                    lg: PROCESS_CAPSULE_PRIMARY_COLOR,
                  },

                  textAlign: isEven ? "right" : "left",

                  overflowWrap: "anywhere",

                  minWidth: 0,

                  maxWidth: "100%",

                  transition: `color ${PROCESS_CAPSULE_HOVER_DURATION_MS}ms ease`,
                }}
              >
                {titleText}
              </Typography>
            </Box>
          </Box>

          {/* =====================================
              DIVIDER BETWEEN TITLE AND DESCRIPTION
          ====================================== */}
          <Box
            className="service-process-divider"
            aria-hidden="true"
            sx={{
              /*
               * Base line stays visible.
               *
               * The white hover line is drawn by ::after and fills
               * from the image side toward the rounded text side.
               */
              position: "relative",

              width: "auto",
              alignSelf: "stretch",

              ml: isEven
                ? {
                    xs: 0,
                    sm: 0,
                    md: 0,
                    lg: -4,
                  }
                : {
                    xs: 2,
                    sm: 4,
                    md: 5,
                    lg: 7,
                  },

              mr: isEven
                ? {
                    xs: 2,
                    sm: 4,
                    md: 5,
                    lg: 7,
                  }
                : {
                    xs: 0,
                    sm: 0,
                    md: 0,
                    lg: -4,
                  },

              height: "2px",

              overflow: "hidden",

              backgroundColor:
                "rgba(0, 144, 160, 0.34)",

              mt: {
                xs: 0.5,
                sm: 0.65,
                md: 0.75,
                lg: 1,
              },

              mb: {
                xs: 0.5,
                sm: 0.65,
                md: 0.75,
                lg: 1,
              },

              opacity: show ? 1 : 0,

              transform: show
                ? "translate3d(0, 0, 0)"
                : "translate3d(0, 12px, 0)",

              transition: prefersReducedMotion
                ? "none"
                : [
                    `opacity ${titleTiming.duration}ms ease`,
                    `transform ${titleTiming.duration}ms cubic-bezier(.2,.8,.2,1)`,
                  ].join(", "),

              transitionDelay: prefersReducedMotion
                ? "0ms"
                : `${titleTiming.delay}ms`,

              willChange: "opacity, transform",

              "&::after": {
                content: '""',

                position: "absolute",

                inset: 0,

                backgroundColor: "#ffffff",

                transform: {
                  xs: "scaleX(1)",
                  sm: "scaleX(1)",
                  md: "scaleX(1)",
                  lg: "scaleX(0)",
                },

                /*
                 * Image-left row:
                 * image is on the left, so fill left to right.
                 *
                 * Image-right row:
                 * image is on the right, so fill right to left.
                 */
                transformOrigin: "left center",

                transition: prefersReducedMotion
                  ? "none"
                   : "transform 1100ms cubic-bezier(.2,.3,.2,1)",

                willChange: "transform",
              },
            }}
          />

          {/* =====================================
              DESCRIPTION
          ====================================== */}
          <Typography
            className="service-process-description"
            sx={{
              width: {
                xs: "100%",
                sm: "88%",
                md: "76%",
                lg: "86%",
              },

              maxWidth: "100%",

              /*
               * The divider now supplies the spacing above the
               * description.
               */
              mt: 0,

              ml: isEven ? "auto" : 0,

              mr: isEven ? 0 : "auto",

              pr: isEven
                ? {
                    xs: 2,
                    sm: 6,
                    md: 5,
                    lg: 7,
                  }
                : {
                    xs: 1.5,
                    sm: 0,
                  },

              pl: isEven
                ? {
                    xs: 1.5,
                    sm: 0,
                  }
                : {
                    xs: 2,
                    sm: 6,
                    md: 5,
                    lg: 7,
                  },

              fontFamily: fonts,
              fontWeight: 400,

              fontSize: {
                xs: 11,
                sm: 13,
                md: 14.5,
                lg: 20,
              },

              lineHeight: {
                xs: 1.45,
                sm: 1.55,
                md: 1.6,
                lg: 1.72,
              },

              color: {
                xs: "#ffffff",
                sm: "#ffffff",
                md: "#ffffff",
                lg: PROCESS_CAPSULE_DEFAULT_TEXT,
              },

              textAlign: isEven ? "right" : "left",

              overflowWrap: "anywhere",

              opacity: show ? 1 : 0,

              transform: show
                ? "translate3d(0, 0, 0)"
                : "translate3d(0, 14px, 0)",

              transition: prefersReducedMotion
                ? `color ${PROCESS_CAPSULE_HOVER_DURATION_MS}ms ease`
                : [
                    `opacity ${textTiming.duration}ms ease`,
                    `transform ${textTiming.duration}ms cubic-bezier(.2,.8,.2,1)`,
                    `color ${PROCESS_CAPSULE_HOVER_DURATION_MS}ms ease`,
                  ].join(", "),

              /*
               * The description entrance still waits for textTiming.delay.
               *
               * The third delay belongs to the color transition and must
               * remain 0ms so hover changes it to white immediately.
               */
              transitionDelay: prefersReducedMotion
                ? "0ms"
                : [
                    `${textTiming.delay}ms`,
                    `${textTiming.delay}ms`,
                    "0ms",
                  ].join(", "),

              willChange: "opacity, transform, color",
            }}
          >
            {text}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

/* ---------- RESPONSIVE ROW CONTROLLER ---------- */
function ProcessRow(props) {
  const { enhancedLayoutMediaQuery = PROCESS_ENHANCED_LAYOUT_MEDIA_QUERY } =
    props;

  const isEnhancedLayout = useMediaQuery(enhancedLayoutMediaQuery);

  /*
   * Tablet landscape and desktop enter this branch.
   *
   * It temporarily renders the current row so the visible design
   * remains unchanged until EnhancedProcessRow is constructed in
   * the next step.
   */
  if (isEnhancedLayout) {
    return <EnhancedProcessRow {...props} />;
  }

  /*
   * Mobile and tablet portrait permanently retain the existing
   * Service Process layout and animation.
   */
  return <LegacyProcessRow {...props} />;
}

export default function ServiceProcess({
  fonts = '"LINE Sans TW","Noto Sans TC",system-ui',
  bg = "background.default",
  boxRevealDirection = DEFAULT_PROCESS_BOX_REVEAL_DIRECTION,
}) {
  const prefersReducedMotion = useMediaQuery(
    "(prefers-reduced-motion: reduce)",
  );
  const [lang, setLang] = useState(getCurrentLang());

  useEffect(() => {
    const onLangChange = () => setLang(getCurrentLang());
    window.addEventListener("app:languageChanged", onLangChange);
    return () =>
      window.removeEventListener("app:languageChanged", onLangChange);
  }, []);

  const sectionTitle = SECTION_TITLE[lang] || SECTION_TITLE.ZH;

  const resolvedBoxRevealDirection =
    normalizeBoxRevealDirection(boxRevealDirection);

  return (
    <Box
      component="section"
      sx={{
        py: {
          xs: 6,
          md: 8,
        },

        bgcolor: bg,

        /*
         * Enhanced capsules extend from the centered row to the
         * viewport edge. Clip only the horizontal overflow so they
         * cannot create a horizontal scrollbar.
         */
        overflowX: "clip",
      }}
    >
      <Container
        maxWidth={false}
        disableGutters
        sx={{ maxWidth: "auto", px: { xs: 2, md: 3 }, mx: "auto" }}
      >
        <Typography
          variant="h4"
          sx={{
            width: "100%",

            m: 0,

            fontFamily: fonts,
            fontWeight: 700,

            fontSize: {
              xs: 24,
              md: 28,
              lg: 40,
            },

            lineHeight: 1.3,

            textAlign: "center",

            /*
             * Create clear separation between the centered section
             * title and the first Service Process row.
             */
            mb: {
              xs: 6,
              sm: 7,
              md: 8,
              lg: 10,
            },

            color: "text.secondary",
          }}
        >
          {sectionTitle}
        </Typography>

        {SERVICE_PROCESS_ITEMS.map((item, index) => (
          <ProcessRow
            key={item.id}
            item={item}
            index={index}
            lang={lang}
            fonts={fonts}
            prefersReducedMotion={prefersReducedMotion}
            enhancedLayoutMediaQuery={PROCESS_ENHANCED_LAYOUT_MEDIA_QUERY}
            boxColor={PROCESS_BOX_COLORS[index % PROCESS_BOX_COLORS.length]}
            boxRevealDirection={resolvedBoxRevealDirection}
            animationTiming={PROCESS_ANIMATION_TIMING}
          />
        ))}
      </Container>
    </Box>
  );
}
